import { Injectable } from '@angular/core';
import { DrLocalStorageService } from '../local-storage/dr-local-storage.service';
import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { DrDateAdapter } from '@app/shared/modules/ang-material-import/ang-material-import.module';
import { CurrencyCode } from '@app/shared/models/currency_code';
import { BehaviorSubject } from 'rxjs';

export interface DrDateInputFormat {
  dateShort: string;
  dateLong: string;
  time: string;
}

export interface DrDateViewFormat {
  datepicker: string;
  piped: string;
  pipedWithTime: string;
  momentDate: string;
  momentDateTime: string;
}

export interface DrCurrencyFormat {
  currency_code: string;
  currency_display_name: string;
  currency_fraction_digits: number;
  currency_symbol: string;
  decimal_separator: string;
  thousands_separator: string;
}

export interface DrLocale {
  shortName: string;
  fullName: string;
  dateInputShortcuts: DrDateInputFormat;
  dateDisplayFormats: DrDateViewFormat;
  currencyFormats: DrCurrencyFormat;
}

export enum DrLocales {
  DE = 'de-DE',
  EN = 'en-US'
}

@Injectable({
  providedIn: 'root'
})
export class DrI18nService {

  public static DE: DrLocale = {
    shortName: 'de',
    fullName: 'de-DE',
    dateInputShortcuts: {
      dateShort: 'DDMMYY',
      dateLong: 'DDMMYYYY',
      time: 'HHmm'
    },
    dateDisplayFormats: {
      datepicker: 'DD.MM.YYYY',
      piped: 'dd.MM.yyyy',
      pipedWithTime: 'dd.MM.yyyy (HH:mm)',
      momentDate: 'DD.MM.YYYY',
      momentDateTime: 'DD.MM.YYYY (HH:mm)'
    },
    currencyFormats: {
      currency_code: CurrencyCode.EUR,
      currency_display_name: CurrencyCode.EUR,
      currency_fraction_digits: 2,
      currency_symbol: '€',
      decimal_separator: ',',
      thousands_separator: '.'
    }
  };

  public static EN: DrLocale = {
    shortName: 'en',
    fullName: 'en-US',
    dateInputShortcuts: {
      dateShort: 'MMDDYY',
      dateLong: 'MMDDYYYY',
      time: 'hhmm'
    },
    dateDisplayFormats: {
      datepicker: 'MM/DD/YYYY',
      piped: 'MM/dd/yyyy',
      pipedWithTime: 'MM/dd/yyyy (hh:mm aa)',
      momentDate: 'MM/DD/YYYY',
      momentDateTime: 'MM/DD/YYYY (hh:mm A)',
    },
    currencyFormats: {
      currency_code: CurrencyCode.EUR,
      currency_display_name: CurrencyCode.EUR,
      currency_fraction_digits: 2,
      currency_symbol: '€',
      decimal_separator: '.',
      thousands_separator: ','
    }
  };

  private static _activeLocale: DrLocales;
  private _drDateAdapter: DrDateAdapter;

  set drDateAdapter(drDateAdapter: DrDateAdapter) {
    this._drDateAdapter = drDateAdapter;
    this.switchLocale();
  }

  get activeLocale() {
    return DrI18nService._activeLocale;
  }

  set activeLocale(locale: DrLocales) {
    DrI18nService._activeLocale = locale;
    this.drLocalStorageService.set(this.drLocalStorageService.STORAGE_KEY_LOCALE, DrI18nService._activeLocale);
    this.switchLocale();
  }

  protected _loadedTransFile: string;

  get loadedTransFile() {
    return this._loadedTransFile;
  }

  public loadedTransFileSubject: BehaviorSubject<string>;

  public static getLocale(identifier: DrLocales): DrLocale {
    switch (identifier) {
      case DrLocales.DE:
        return DrI18nService.DE;
      case DrLocales.EN:
        return DrI18nService.EN;
      default:
        return DrI18nService.DE;
    }
  }

  public static dateTimeToDateOnlyString(date: Date): string | null {
    return date ? moment(date).format('YYYY-MM-DD') : null;
  }

  public static dateTimeString(date: Date) {
    const daMoment: moment.Moment = moment(date);
    const localeDateTimeFormat = DrI18nService.getActiveLocaleInformation().dateDisplayFormats.momentDateTime;
    return daMoment.format(localeDateTimeFormat);
  }

  public static dateString(date: Date) {
    const daMoment: moment.Moment = moment(date);
    const localeDateTimeFormat = DrI18nService.getActiveLocaleInformation().dateDisplayFormats.momentDate;
    return daMoment.format(localeDateTimeFormat);
  }

  constructor(private drLocalStorageService: DrLocalStorageService,
              private translate: TranslateService) {

    this.loadedTransFileSubject = new BehaviorSubject(null);

    this.translate.onLangChange.subscribe(result => {
        if (result) {
            this._loadedTransFile = result.lang;
            this.loadedTransFileSubject.next(this._loadedTransFile);
            console.log(`DrI18nService - Translation file '${this._loadedTransFile}.json' loaded`);
        }
    });

    this.translate.setDefaultLang(DrI18nService.DE.shortName);
    this.activeLocale = this.drLocalStorageService.get(this.drLocalStorageService.STORAGE_KEY_LOCALE) || DrLocales.DE;
  }

  private switchLocale(): void {
    this.translate.use(DrI18nService.getActiveLocaleInformation().shortName);
    moment.locale(DrI18nService.getActiveLocaleInformation().shortName);
    if (this._drDateAdapter) {
      this._drDateAdapter.drDateLocale = DrI18nService.getActiveLocaleInformation();
      console.log(`DrI18nService - DrDateAdapter set to '${DrI18nService.getActiveLocaleInformation().fullName}'. Loading translation file`);
    } else {
      console.log('DrI18nService - DrDateAdapter not set');
    }
  }

  public static getActiveLocaleInformation(): DrLocale {
    return DrI18nService.getLocale(DrI18nService._activeLocale);
  }

}

export function LocaleIdFactory(drI18nService: DrI18nService) {
  return drI18nService.activeLocale;
}

